import React, { useState } from 'react';

import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import EmailFieldWithCheck from '@/components/form/EmailFieldWithCheck.tsx';
import ModalAlert from '@/components/ui/ModalAlert.tsx';
import { validateEmail } from '@/utils/misc';

import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Container,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSignUp } from '@/lib/api/auth';
import { Link, useNavigate } from 'react-router-dom';
import { PublicOnlyGuard } from '@/features/auth/PublicOnlyGuard.tsx';
import { AxiosError } from 'axios';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  ANALYTICS_PLATFORM,
  getAcquisitionChannel,
  getSignupAttribution,
  trackProductEvent,
} from '@/lib/analytics/product-events.ts';

type UserRole = 'psi' | 'investor';

interface FormData {
  name: string;
  email: string;
  role: UserRole | '';
  phone: string;
  comments: string;
  active: boolean;
}

const AltaForms = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [openModal, setOpenModal] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [password2, setPassword2] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleOpenModal = (message: string) => {
    setIsLoading(false);
    setOpenModal(true);
    setMensajeModal(message);
  };

  const handleClose = () => {
    setOpenModal(false);
    if (isValid) {
      navigate('/');
    }
  };

  const [mensajeModal, setMensajeModal] = useState('');

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: '',
    phone: '',
    comments: '',
    active: true,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { mutateAsync: signUp } = useSignUp();

  const registerUser = async (
    email: string,
    password: string,
    role: UserRole
  ): Promise<{ error?: string }> => {
    try {
      await signUp({
        email,
        password,
        role,
        name: formData.name,
        attribution: getSignupAttribution(),
      });
      return { error: undefined };
    } catch (error: unknown) {
      console.error('Error en el proceso de alta:', error);
      if (error instanceof AxiosError && error.response?.status === 409) {
        return { error: 'Ya existe una cuenta con ese email.' };
      }
      if (error instanceof AxiosError && error.response?.status === 500) {
        return { error: 'Error interno del servidor. Inténtalo más tarde.' };
      }
      return { error: 'Error al conectar con el servidor.' };
    }
  };

  const [step, setStep] = useState(0);

  const handleRoleSelection = (role: UserRole) => {
    setFormData({ ...formData, role: role });
    setStep(1);
  };

  const handleGoBack = () => {
    setFormData({ ...formData, role: '' });
    setStep(0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({ ...formData, email: value.trim() });
    checkEmail();
  };

  const validateName = () => {
    if (!formData.name) {
      setErrors((prevErrors) => ({ ...prevErrors, name: 'El nombre es obligatorio' }));
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  const checkEmail = () => {
    if (!formData.email) {
      setErrors((prevErrors) => ({ ...prevErrors, email: 'El email es obligatorio' }));
    } else if (!validateEmail(formData.email)) {
      // aquí sí llamas a la de utils
      setErrors((prevErrors) => ({ ...prevErrors, email: 'El email no es válido' }));
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const validatePassword = (password: string, confirmPassword: string) => {
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsValid(false);
    trackEvent('sign_up_submitted', { step: 'details' });

    if (!acceptTerms) {
      handleOpenModal('Debe aceptar los términos y condiciones para continuar.');
      trackEvent('sign_up_validation_error', { reason: 'terms_not_accepted' });
      setIsLoading(false);
      return;
    }

    // Validar campos
    const nameValid = formData.name.trim() !== '';
    const emailValid = formData.email.trim() !== '' && validateEmail(formData.email);
    const passwordValid = password2.length >= 6;
    const passwordsMatch = password2 === confirmPassword;
    const role = formData.role;

    if (!nameValid) {
      handleOpenModal('El nombre es obligatorio.');
      trackEvent('sign_up_validation_error', { reason: 'missing_name' });
      setIsLoading(false);
      return;
    }

    if (!emailValid) {
      handleOpenModal('Ingrese un email válido.');
      trackEvent('sign_up_validation_error', { reason: 'invalid_email' });
      setIsLoading(false);
      return;
    }

    if (!passwordValid) {
      handleOpenModal('La contraseña debe tener al menos 6 caracteres.');
      trackEvent('sign_up_validation_error', { reason: 'weak_password' });
      setIsLoading(false);
      return;
    }

    if (!passwordsMatch) {
      handleOpenModal('Las contraseñas no coinciden.');
      trackEvent('sign_up_validation_error', { reason: 'password_mismatch' });
      setIsLoading(false);
      return;
    }

    if (!formData.name || !formData.email || !role) {
      handleOpenModal('Por favor, complete todos los campos obligatorios.');
      trackEvent('sign_up_validation_error', { reason: 'missing_required_fields' });
      setIsLoading(false);
      return;
    }

    const email = formData.email.toLowerCase();

    try {
      // 1. Registro y login
      const { error } = await registerUser(email, password2, role);

      if (error) {
        handleOpenModal(error || 'Error al registrar el usuario.');
        trackEvent('sign_up_failed', { reason: 'server_error' });
        return;
      }

      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        comments: '',
        active: true,
      });

      setIsValid(true);
      trackProductEvent(trackEvent, 'signup_completed', {
        role,
        signup_method: 'email_password',
        acquisition_channel: getAcquisitionChannel(),
        platform: ANALYTICS_PLATFORM,
      });
      handleOpenModal('Tu solicitud ha sido recibida. Revisa tu email para activar tu perfil.');
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      handleOpenModal('Ocurrió un error al conectar con el servidor.');
      trackEvent('sign_up_failed', { reason: 'network_error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicOnlyGuard>
      {isLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="fixed"
          top="0"
          left="0"
          width="100%"
          height="100%"
          zIndex="modal"
          bgcolor="rgba(255, 255, 255, 0.8)"
        >
          <CircularProgress />
        </Box>
      )}
      {step === 0 ? (
        <Container
          maxWidth="md"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 3, md: 4 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ width: '100%' }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
              <img
                src="/images/logos/inversorhub-logo.png"
                alt="InversorHUB Logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Typography
              variant="h4"
              component="h1"
              align="center"
              gutterBottom
              sx={{ mb: { xs: 2, md: 3 }, fontWeight: 600 }}
            >
              ¿Cómo quieres registrarte?
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: { xs: 3, md: 4 } }}
            >
              Selecciona tu perfil para comenzar
            </Typography>

            <Grid container spacing={3}>
              {/* Investor Card */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 4,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleRoleSelection('investor')}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    {/* Gradient header for mobile, image for desktop */}
                    <Box
                      sx={{
                        display: { xs: 'block', sm: 'none' },
                        backgroundColor: 'primary.main',
                        height: '80px',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2,
                          fontSize: '2rem',
                        }}
                      >
                        💼
                      </Box>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image="/images/inversor_icon.png"
                        alt="Inversor"
                        sx={{ objectFit: 'cover' }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3, pt: { xs: 4, sm: 3 } }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        gutterBottom
                        fontWeight="bold"
                        sx={{
                          color: 'primary.main',
                        }}
                      >
                        Inversor
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Persona o entidad que destina recursos económicos a la compra de bienes
                        inmuebles con el propósito de obtener beneficios económicos.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* PSI Card */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 4,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleRoleSelection('psi')}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'block', sm: 'none' },
                        backgroundColor: 'primary.main',
                        height: '80px',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2,
                          fontSize: '2rem',
                        }}
                      >
                        🏢
                      </Box>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <CardMedia
                        component="img"
                        height="180"
                        image="/images/psi_icon.png"
                        alt="PSI"
                        sx={{ objectFit: 'cover' }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3, pt: { xs: 4, sm: 3 } }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        gutterBottom
                        fontWeight="bold"
                        sx={{
                          color: 'primary.main',
                        }}
                      >
                        PSI
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Profesional especializado en la búsqueda, selección y negociación de
                        propiedades inmobiliarias para clientes.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            {/* Link to login */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MuiLink
                component={Link}
                to="/login"
                underline="hover"
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                ¿Ya tienes cuenta? Inicia sesión
              </MuiLink>
            </Box>
          </Box>
        </Container>
      ) : (
        <Container
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 2, md: 4 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              boxShadow: { xs: 'none', sm: 6 },
              background: { xs: 'transparent', sm: 'background.paper' },
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography component="h1" variant="h5" fontWeight="600">
                  Crear cuenta
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Completa tus datos para registrarte
                </Typography>
              </Box>

              <Box component="form" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextFieldWithCheck
                      id="name"
                      name="name"
                      label="Nombre completo"
                      variant="outlined"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={validateName}
                      error={!!errors.name}
                      helperText={errors.name}
                      fullWidth
                      FormHelperTextProps={{ style: { fontSize: '0.75rem', color: '#d32f2f' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EmailFieldWithCheck
                      id="email"
                      name="email"
                      label="Email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      onBlur={checkEmail}
                      error={!!errors.email}
                      helperText={errors.email}
                      FormHelperTextProps={{ style: { fontSize: '0.75rem', color: '#d32f2f' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Contraseña"
                      id="pass"
                      name="pass"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={password2}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPassword2(value);
                        validatePassword(value, confirmPassword);
                      }}
                      fullWidth
                      required
                      error={!!passwordError}
                      helperText={passwordError || 'Mínimo 6 caracteres'}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      FormHelperTextProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Confirmar contraseña"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="pass2"
                      name="pass2"
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => {
                        const value = e.target.value;
                        setConfirmPassword(value);
                        validatePassword(password2, value);
                      }}
                      fullWidth
                      required
                      error={!!passwordError}
                      helperText={passwordError}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={handleClickShowConfirmPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      FormHelperTextProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Acepto recibir comunicaciones de InversorHub y he leído y acepto los{' '}
                          <MuiLink
                            component={Link}
                            to="https://www.inversorhub.com/legal"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 500,
                            }}
                          >
                            términos y condiciones
                          </MuiLink>
                        </Typography>
                      }
                      sx={{
                        alignItems: 'flex-start',
                        '& .MuiCheckbox-root': {
                          pt: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      type="submit"
                      fullWidth
                      disabled={isLoading}
                      sx={{ mb: 1 }}
                    >
                      Crear cuenta
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      onClick={handleGoBack}
                      disabled={isLoading}
                    >
                      Volver
                    </Button>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <MuiLink
                    component={Link}
                    to="/login"
                    underline="hover"
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    ¿Ya tienes cuenta? Inicia sesión
                  </MuiLink>
                </Box>
              </Box>
            </Box>
          </Paper>
          <ModalAlert open={openModal} message={mensajeModal} onClose={handleClose} />
        </Container>
      )}
    </PublicOnlyGuard>
  );
};

export default AltaForms;
